import { DurationUnit, FormField, InputField, SelectField, SwitchField } from "common";
import { Formik, FormikProps } from "formik";
import { noop } from "lodash";
import React, { forwardRef } from "react";
import { KioskGeneralFieldConfig, KioskGeneralPreferences, KioskPreferenceType } from "types";
import { TimePickerPopover } from "@/components/Common/TimePickerPopover";
import { ContentLayout } from "@/components/Layout/ContentLayout";

type KioskGeneralPreferencesFieldsProps = {
    initialValues: KioskGeneralPreferences;
};

const homeScreenButtons: KioskGeneralFieldConfig[] = [
    {
        name: KioskPreferenceType.KIOSK_HOME_SHOW_PURCHASE_BTN,
        label: "Show New Booking Button",
        children: "Display the New Booking Button on the Home Screen",
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
    {
        name: KioskPreferenceType.KIOSK_HOME_SHOW_CHECKIN_BTN,
        label: "Show Check-In Button",
        children: "Display the Check-In Button on the Home Screen",
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
    {
        name: KioskPreferenceType.KIOSK_HOME_SHOW_WAIVER_BTN,
        label: "Show Sign Waiver Button",
        children: "Display the Sign Waiver Button on the Home Screen",
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
];

const requiredFields: KioskGeneralFieldConfig[] = [
    {
        name: KioskPreferenceType.KIOSK_PURCHASE_NAME_REQUIRED,
        label: "Show Name Field for New Purchases",
        children: null,
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
    {
        name: KioskPreferenceType.KIOSK_PURCHASE_PHONE_REQUIRED,
        label: "Show Phone Number Field for New Purchases",
        children: null,
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
    {
        name: KioskPreferenceType.KIOSK_PURCHASE_EMAIL_REQUIRED,
        label: "Show Email Address Field for New Purchases",
        children: null,
        tooltip: undefined,
        field: "switch",
        fieldAttr: undefined,
    },
];

const generalPreferenceFields = {
    KIOSK_SETTINGS_PASSWORD: {
        name: KioskPreferenceType.KIOSK_SETTINGS_PASSWORD,
        label: "Settings Screen Password",
        children: null,
        tooltip: "Users will be required to enter this code to enter the hardware settings screen on the kiosk.",
        field: "text",
    },
};

function isDropdownField(
    field: string | undefined,
    fieldAttribute: unknown,
): fieldAttribute is { values: Array<Record<string, string>> } {
    if (field !== "dropdown") return false;
    if (!fieldAttribute || typeof fieldAttribute !== "object") return false;
    // Use type assertion to access values safely
    return (
        "values" in (fieldAttribute as { values?: unknown }) &&
        Array.isArray((fieldAttribute as { values?: unknown }).values)
    );
}

export const KioskGeneralPreferencesFields = forwardRef<
    FormikProps<KioskGeneralPreferences>,
    KioskGeneralPreferencesFieldsProps
>(({ initialValues }, ref) => {
    return (
        <Formik enableReinitialize innerRef={ref} initialValues={initialValues} onSubmit={noop}>
            {({ values, setFieldValue }) => {
                return (
                    <ContentLayout.Section title="General">
                        {/* Home Screen Buttons */}
                        {homeScreenButtons.map(({ name, label, children, field, tooltip, fieldAttr }) => (
                            <div key={name} data-testid={`prefs-kiosk-${label}-section`}>
                                <FormField name={name} label={label} tooltip={tooltip ?? null}>
                                    {field === "switch" ? (
                                        <SwitchField
                                            className="!items-start"
                                            isChecked={!!values[name]}
                                            onChange={(value) => {
                                                setFieldValue(name, value);
                                            }}
                                        >
                                            {children ?? label}
                                        </SwitchField>
                                    ) : field === "text" ? (
                                        <InputField
                                            className="!items-start"
                                            value={values[name] as string}
                                            onChange={(value) => {
                                                setFieldValue(name, value);
                                            }}
                                        />
                                    ) : isDropdownField(field, fieldAttr) ? (
                                        <>
                                            {children}
                                            <SelectField>
                                                {fieldAttr.values.map((item: Record<string, string>) => {
                                                    const [key, value] = Object.entries(item)[0];
                                                    return (
                                                        <option key={key} value={key}>
                                                            {value}
                                                        </option>
                                                    );
                                                })}
                                            </SelectField>
                                        </>
                                    ) : null}
                                </FormField>
                            </div>
                        ))}

                        {/* Automatic Idle Reset */}
                        <div className="flex items-center">
                            <FormField
                                name={KioskPreferenceType.KIOSK_IDLE_RESET_TIME_ENABLED}
                                label="Automatic Idle Reset"
                            >
                                <SwitchField
                                    isChecked={!!values[KioskPreferenceType.KIOSK_IDLE_RESET_TIME_ENABLED]}
                                    onChange={(isChecked) => {
                                        setFieldValue(KioskPreferenceType.KIOSK_IDLE_RESET_TIME_ENABLED, isChecked);
                                    }}
                                >
                                    {values[KioskPreferenceType.KIOSK_IDLE_RESET_TIME_ENABLED]
                                        ? "Return the kiosk app to its starting screen after"
                                        : null}
                                </SwitchField>
                            </FormField>

                            {values[KioskPreferenceType.KIOSK_IDLE_RESET_TIME_ENABLED] ? (
                                <p className="font-normal text-gray-darker">
                                    <TimePickerPopover
                                        unitOptions={[DurationUnit.MINUTE]}
                                        value={values[KioskPreferenceType.KIOSK_IDLE_RESET_TIME]}
                                        onApply={(value) => {
                                            setFieldValue(KioskPreferenceType.KIOSK_IDLE_RESET_TIME, value);
                                        }}
                                    />
                                    of inactivity
                                </p>
                            ) : null}
                        </div>

                        {/* KIOSK_SETTINGS_PASSWORD */}
                        <FormField
                            name={generalPreferenceFields.KIOSK_SETTINGS_PASSWORD.name}
                            label={generalPreferenceFields.KIOSK_SETTINGS_PASSWORD.label}
                            tooltip={generalPreferenceFields.KIOSK_SETTINGS_PASSWORD.tooltip}
                        >
                            <InputField
                                className="!items-start"
                                value={values[generalPreferenceFields.KIOSK_SETTINGS_PASSWORD.name] as string}
                                onChange={(value) =>
                                    setFieldValue(generalPreferenceFields.KIOSK_SETTINGS_PASSWORD.name, value)
                                }
                            />
                        </FormField>

                        {/* Required Fields */}
                        {requiredFields.map(({ name, label, children, field, tooltip, fieldAttr }) => (
                            <div key={name} data-testid={`prefs-kiosk-${label}-section`}>
                                <FormField name={name} label={label} tooltip={tooltip ?? null}>
                                    {field === "switch" ? (
                                        <SwitchField
                                            className="!items-start"
                                            isChecked={!!values[name]}
                                            onChange={(value) => {
                                                setFieldValue(name, value);
                                            }}
                                        >
                                            {children ?? label}
                                        </SwitchField>
                                    ) : field === "text" ? (
                                        <InputField
                                            className="!items-start"
                                            value={values[name] as string}
                                            onChange={(value) => {
                                                setFieldValue(name, value);
                                            }}
                                        />
                                    ) : isDropdownField(field, fieldAttr) ? (
                                        <>
                                            {children}
                                            <SelectField>
                                                {(fieldAttr as { values: Array<Record<string, string>> })?.values?.map(
                                                    (item) => {
                                                        const [key, value] = Object.entries(item)[0];
                                                        return (
                                                            <option key={key} value={key}>
                                                                {value}
                                                            </option>
                                                        );
                                                    },
                                                )}
                                            </SelectField>
                                        </>
                                    ) : null}
                                </FormField>
                            </div>
                        ))}
                    </ContentLayout.Section>
                );
            }}
        </Formik>
    );
});
